import { createTRPCReact, httpBatchLink } from "@trpc/react-query";
import { createTRPCProxyClient, TRPCClientError } from "@trpc/client";
// @ts-ignore - API package has different TypeScript settings
import type { AppRouter } from "../../../../api/src/routers/appRouter";
import { tokenStorage } from "../../utils/auth/tokenStorage";
import config from "../../config/environment";
import { createTrpcLinksWithRefresh } from "./trpcWithRefresh";

// Get the full tRPC endpoint URL
const getTrpcUrl = () => `${config.api.url}${config.api.trpcEndpoint}`;

// Create the React hooks version of tRPC
export const trpc = createTRPCReact<AppRouter>();

// Create a vanilla tRPC client for direct usage outside of React components
// @ts-ignore - This is a workaround for TypeScript errors with tRPC
export const vanillaClient = createTRPCProxyClient<AppRouter>({
  links: createTrpcLinksWithRefresh(),
});

// Create tRPC client links (deprecated - use createTrpcLinksWithRefresh instead)
export function createTrpcLinks() {
  return [
    // Main HTTP link with token handling
    httpBatchLink({
      url: getTrpcUrl(),
      headers: () => {
        const headers: Record<string, string> = {};

        // Add authorization header if token exists
        const token = tokenStorage.getAuthToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        return headers;
      },
    }),
  ];
}

// Export the new enhanced links function for use in the provider
export { createTrpcLinksWithRefresh };

// Handle tRPC errors
export const handleTrpcError = (
  error: unknown,
): { message: string; status?: number } => {
  // Default error message
  let message = "An unexpected error occurred";
  let status: number | undefined = undefined;

  if (error instanceof TRPCClientError) {
    // Extract error details
    const trpcError = error as TRPCClientError<any>;
    status = trpcError.data?.httpStatus;

    // Use message from the error if available
    if (trpcError.message) {
      message = trpcError.message;
    }

    // Check for structured error data
    if (trpcError.data?.message) {
      message = trpcError.data.message;
    }

    // Handle token expiration
    if (status === 401) {
      // Token has expired, handle accordingly
      if (!tokenStorage.getRefreshToken()) {
        window.dispatchEvent(new CustomEvent("auth:expired"));
      }
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  // Log the error in development
  if (config.environment === "development") {
    console.error("TRPC Error:", error);
  }

  return { message, status };
};
